import { useState } from "react";
import {
  createPayout,
  createWalletPayout,
  createAccountAttachmentUploadUrl,
  finalizeAccountAttachment,
  useAction,
} from "wasp/client/operations";
import { trackPayoutAdded } from '../client/utils/posthog';
import { useToast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../client/components/ui/select";
import { Button } from "../client/components/ui/button";
import { Trash2, Plus, Upload, X } from "lucide-react";
import PayoutAmountFields from "../trading-accounts/PayoutAmountFields";
import { EMPTY_PAYOUT_VALUES, resolvePayoutForm, type PayoutFormValues } from "../trading-accounts/payoutForm";

const ACTIVE_STATUSES = ["pa", "challenge", "live"];

function todayISO(): string {
  return new Date().toISOString().split("T")[0];
}

function makeRow(): PayoutRow {
  return { id: crypto.randomUUID(), accountId: "", date: todayISO(), amounts: EMPTY_PAYOUT_VALUES, certFile: null };
}

interface PayoutRow {
  id: string;
  accountId: string;
  date: string;
  /** Brut / net : voir payoutForm.ts. */
  amounts: PayoutFormValues;
  certFile: File | null;
}

async function uploadCert(file: File, accountId: string, payoutId: string) {
  const allowed = ["application/pdf", "image/png", "image/jpeg"];
  if (!allowed.includes(file.type) || file.size > 5 * 1024 * 1024) {
    throw new Error("cert validation failed");
  }
  const { s3UploadUrl, s3UploadFields, s3Key } = await createAccountAttachmentUploadUrl({
    accountId,
    fileName: file.name,
    mimeType: file.type as any,
  });
  const fd = new FormData();
  Object.entries(s3UploadFields).forEach(([k, v]) => fd.append(k, v as string));
  fd.append("file", file);
  const s3Res = await fetch(s3UploadUrl, { method: "POST", body: fd });
  if (!s3Res.ok) throw new Error("s3 upload failed");
  await finalizeAccountAttachment({
    accountId,
    payoutId,
    type: "payout",
    name: file.name,
    s3Key,
    mimeType: file.type as any,
  });
}

interface AddPayoutModalProps {
  open: boolean;
  onClose: () => void;
  accounts: { id: string; name: string; status: string }[];
  onSuccess: () => void;
  /**
   * Ouverte depuis la carte Wallet (« Retrait vers l'externe ») : le payout est
   * un retrait du wallet de cette prop firm (walletOperations.createWalletPayout),
   * compté en revenu sans baisser le solde du compte.
   */
  walletPropFirmTemplateId?: string;
}

export default function AddPayoutModal({ open, onClose, accounts, onSuccess, walletPropFirmTemplateId }: AddPayoutModalProps) {
  const t = useTranslation();
  const { toast } = useToast();
  const createPayoutFn = useAction(createPayout);
  const createWalletPayoutFn = useAction(createWalletPayout);

  const [rows, setRows] = useState<PayoutRow[]>([makeRow()]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const activeAccounts = accounts.filter((a) => ACTIVE_STATUSES.includes(a.status));

  const isValidRow = (r: PayoutRow) => r.accountId !== "" && r.date !== "" && resolvePayoutForm(r.amounts).ok;
  const validRows = rows.filter(isValidRow);

  function updateRow(id: string, patch: Partial<PayoutRow>) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function addRow() {
    setRows((prev) => [...prev, makeRow()]);
  }

  function removeRow(id: string) {
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));
  }

  function handleClose() {
    setRows([makeRow()]);
    onClose();
  }

  async function handleSubmit() {
    const valid = rows.filter(isValidRow);
    if (valid.length === 0) {
      if (rows.some((r) => { const res = resolvePayoutForm(r.amounts); return !res.ok && res.reason === "gross_below_net"; })) {
        toast({ title: t.tradingAccounts.payoutGrossBelowNet, variant: "destructive" });
      }
      return;
    }
    setIsSubmitting(true);
    try {
      let certFailures = 0;
      await Promise.all(
        valid.map(async (r) => {
          const resolved = resolvePayoutForm(r.amounts);
          if (!resolved.ok) return;
          const payoutArgs = {
            accountId: r.accountId,
            date: r.date,
            // NET reçu / BRUT débité du compte (voir payoutAmounts.ts).
            amount: resolved.amount,
            grossAmount: resolved.grossAmount,
          };
          const created = walletPropFirmTemplateId
            ? await createWalletPayoutFn({ ...payoutArgs, propFirmTemplateId: walletPropFirmTemplateId })
            : await createPayoutFn(payoutArgs);
          if (r.certFile) {
            try {
              await uploadCert(r.certFile, r.accountId, created.id);
            } catch (err) {
              console.error("cert upload failed for payout", created.id, err);
              certFailures++;
            }
          }
        }),
      );
      if (certFailures > 0) {
        toast({
          title: t.propFirmManager.addPayoutSuccessWithCertFailures
            .replace("{n}", String(valid.length))
            .replace("{f}", String(certFailures)),
          variant: "destructive",
        });
      } else {
        toast({
          title: t.propFirmManager.addPayoutSuccess.replace("{n}", String(valid.length)),
        });
      }
      trackPayoutAdded();
      onSuccess();
      handleClose();
    } catch {
      toast({ title: t.tradingAccounts.error, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{t.propFirmManager.addPayoutModalTitle}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {activeAccounts.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-2">
              {t.propFirmManager.addPayoutNoActiveAccounts}
            </p>
          )}
          {activeAccounts.length > 0 && rows.map((row, idx) => (
            <div key={row.id} className="grid grid-cols-[1fr_120px_260px_150px_32px] gap-2 items-end">
              <div>
                {idx === 0 && (
                  <label className="text-xs text-muted-foreground mb-1 block">
                    {t.propFirmManager.addPayoutAccount}
                  </label>
                )}
                <Select value={row.accountId} onValueChange={(v) => updateRow(row.id, { accountId: v })}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder={t.propFirmManager.addPayoutAccount} />
                  </SelectTrigger>
                  <SelectContent>
                    {activeAccounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                {idx === 0 && (
                  <label className="text-xs text-muted-foreground mb-1 block">
                    {t.propFirmManager.addPayoutDate}
                  </label>
                )}
                <input
                  type="date"
                  value={row.date}
                  onChange={(e) => updateRow(row.id, { date: e.target.value })}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Brut + Net regroupés */}
              <PayoutAmountFields
                values={row.amounts}
                onChange={(amounts) => updateRow(row.id, { amounts })}
                hideLabels={idx > 0}
                hideHints
                idPrefix={`pfm-payout-${row.id}`}
              />

              <div>
                {idx === 0 && (
                  <label className="text-xs text-muted-foreground mb-1 block">
                    {t.propFirmManager.addPayoutCertificate}
                  </label>
                )}
                {row.certFile ? (
                  <div className="h-9 flex items-center justify-between gap-1 rounded-md border border-input bg-background px-2 text-sm">
                    <span className="truncate text-xs" title={row.certFile.name}>
                      {row.certFile.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => updateRow(row.id, { certFile: null })}
                      className="text-muted-foreground hover:text-destructive shrink-0"
                      aria-label={t.tradingAccounts.cancel}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <label className="h-9 flex items-center justify-center gap-1 rounded-md border border-dashed border-input bg-background px-2 text-xs text-muted-foreground hover:bg-muted cursor-pointer transition-colors">
                    <Upload className="h-3.5 w-3.5" />
                    <span>{t.propFirmManager.addPayoutCertificatePick}</span>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                      onChange={(e) => updateRow(row.id, { certFile: e.target.files?.[0] ?? null })}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              <button
                type="button"
                onClick={() => removeRow(row.id)}
                disabled={rows.length === 1}
                className="h-9 w-8 flex items-center justify-center text-muted-foreground hover:text-destructive disabled:opacity-30 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-4">
          {activeAccounts.length > 0 && (
            <Button type="button" variant="ghost" size="sm" onClick={addRow}>
              <Plus className="h-4 w-4 mr-1" />
              {t.propFirmManager.addPayoutLine}
            </Button>
          )}

          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={handleClose}>
              {t.tradingAccounts.cancel}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSubmit}
              disabled={isSubmitting || validRows.length === 0}
            >
              {t.propFirmManager.addPayoutSave.replace("{n}", String(validRows.length))}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
